/**
 * SEO Metadata utility for eNEW Studio website
 * Provides comprehensive metadata management for search engine optimization
 */

import type { NewsItem } from '../data/news';

/** 本番のオリジン。OGP 画像・canonical・JSON-LD の絶対 URL に使う */
export const SITE_ORIGIN = 'https://enew.studio';
/**
 * 共通の OGP 画像（scripts/ogp/generate.mjs で生成、public/ogp/）。
 * public/ は長期キャッシュで配信されるので、絵を差し替えるときはファイル名を変える
 */
export const OGP_IMAGE_URL = `${SITE_ORIGIN}/ogp/site.png`;

/** 記事専用の OGP 画像があればその絶対 URL、無ければ共通画像 */
const articleImageUrl = (item: NewsItem): string =>
  item.ogImage ? `${SITE_ORIGIN}${item.ogImage}` : OGP_IMAGE_URL;
/** Organization / publisher のロゴ画像（public/ 直下の既存 PNG） */
export const LOGO_IMAGE_URL = `${SITE_ORIGIN}/android-chrome-512x512.png`;

export interface SEOMetadata {
  title: string;
  description: string;
  keywords: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  canonicalUrl?: string;
  /** true なら robots を noindex にする（存在しない記事など） */
  noindex?: boolean;
  structuredData?: Record<string, unknown>;
}

export interface CompanyStructuredData {
  '@context': string;
  '@type': string;
  name: string;
  description: string;
  url: string;
  logo: string;
  foundingDate: string;
  address: {
    '@type': string;
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  contactPoint: {
    '@type': string;
    contactType: string;
    email: string;
  };
  sameAs: string[];
}

/**
 * Default SEO metadata for eNEW Studio
 */
export const defaultSEOMetadata: SEOMetadata = {
  title: 'eNEW Studio｜AWS 設計・構築・移行、再販・請求代行｜金沢',
  description:
    '元 AWS ソリューションアーキテクトが、AWS の設計・構築・移行、再販・請求代行、CI/CD、Claude Code・Kiro 導入を金沢から全国へ支援します。',
  keywords: [
    'クラウドソリューション',
    'システム開発',
    'AWS',
    'eNEW Studio',
    'DX',
    'デジタルトランスフォーメーション',
    'クラウド移行',
    'システム構築',
    'AWS 再販',
    '請求代行',
    '技術顧問',
    'Claude Code',
  ],
  ogTitle: 'eNEW Studio｜AWS 設計・構築・移行、再販・請求代行｜金沢',
  ogDescription:
    '元 AWS ソリューションアーキテクトが、AWS の設計・構築・移行、再販・請求代行、CI/CD、Claude Code・Kiro 導入を金沢から全国へ支援します。',
  ogImage: OGP_IMAGE_URL,
  twitterCard: 'summary_large_image',
  canonicalUrl: 'https://enew.studio',
};

/**
 * Route-based SEO metadata for page-level optimization
 * Implements requirements 3.1, 3.2, 3.3, 3.4
 */
export const routeSEOMetadata: Record<string, SEOMetadata> = {
  '/': {
    title: 'eNEW Studio｜AWS 設計・構築・移行、再販・請求代行｜金沢',
    description:
      '元 AWS ソリューションアーキテクトが、AWS の設計・構築・移行、再販・請求代行、CI/CD、Claude Code・Kiro 導入を金沢から全国へ支援します。',
    keywords: [
      'クラウドソリューション',
      'システム開発',
      'AWS',
      'eNEW Studio',
      'DX',
      'デジタルトランスフォーメーション',
      'クラウド移行',
      'システム構築',
      'AWS 再販',
      '請求代行',
      '技術顧問',
      'Claude Code',
    ],
    ogTitle: 'eNEW Studio｜AWS 設計・構築・移行、再販・請求代行｜金沢',
    ogDescription:
      '元 AWS ソリューションアーキテクトが、AWS の設計・構築・移行、再販・請求代行、CI/CD、Claude Code・Kiro 導入を金沢から全国へ支援します。',
    ogImage: OGP_IMAGE_URL,
    twitterCard: 'summary_large_image',
    canonicalUrl: 'https://enew.studio/',
  },
  '/privacy': {
    title: 'プライバシーポリシー - eNEW Studio',
    description:
      '株式会社eNEW Studioの個人情報保護方針。個人情報の収集・利用目的、第三者提供、Cookie使用、お問い合わせ対応について説明します。',
    keywords: [
      'クラウドソリューション',
      'システム開発',
      'AWS',
      'eNEW Studio',
      'DX',
      'デジタルトランスフォーメーション',
      'クラウド移行',
      'システム構築',
      'プライバシーポリシー',
      '個人情報保護',
      'Cookie',
      'データ保護',
      '個人情報取扱い',
      '情報セキュリティ',
    ],
    ogTitle: 'プライバシーポリシー - eNEW Studio',
    ogDescription:
      'eNEW Studioの個人情報保護方針。お客様の個人情報を適切に取り扱い、プライバシーを保護するための取り組みについて詳しく説明します。',
    ogImage: OGP_IMAGE_URL,
    twitterCard: 'summary_large_image',
    canonicalUrl: 'https://enew.studio/privacy',
  },
  '/contact': {
    title: 'お問い合わせ | eNEW Studio',
    description:
      'eNEW Studioへのお問い合わせはこちらから。サービスに関するご質問、パートナーシップのご相談など、お気軽にお問い合わせください。',
    keywords: [
      'お問い合わせ',
      'eNEW Studio',
      'コンタクト',
      '相談',
      'クラウドソリューション',
      'システム開発',
      'AWS',
    ],
    ogTitle: 'お問い合わせ | eNEW Studio',
    ogDescription: 'eNEW Studioへのお問い合わせフォーム',
    ogImage: OGP_IMAGE_URL,
    twitterCard: 'summary_large_image',
    canonicalUrl: 'https://enew.studio/contact',
  },
  '/news': {
    title: 'お知らせ | eNEW Studio',
    description: '会社からのお知らせと、登壇や執筆の記録を載せていきます。',
    keywords: ['お知らせ', 'ニュース', 'eNEW Studio', 'AWS'],
    ogTitle: 'お知らせ | eNEW Studio',
    ogDescription: '会社からのお知らせと、登壇や執筆の記録を載せていきます。',
    ogImage: OGP_IMAGE_URL,
    twitterCard: 'summary_large_image',
    canonicalUrl: `${SITE_ORIGIN}/news`,
  },
};

/**
 * お知らせ記事ページのメタデータ（仕様 §5）
 */
export const getSEOMetadataForArticle = (item: NewsItem): SEOMetadata => {
  const description = item.summary ?? item.title;
  return {
    title: `${item.shortTitle ?? item.title} | eNEW Studio`,
    description,
    keywords: ['お知らせ', 'eNEW Studio', 'AWS'],
    ogTitle: item.title,
    ogDescription: description,
    ogImage: articleImageUrl(item),
    ogType: 'article',
    twitterCard: 'summary_large_image',
    canonicalUrl: `${SITE_ORIGIN}/news/${item.slug}`,
  };
};

/**
 * 存在しない記事（未知の slug）のメタデータ。検索エンジンには載せない
 */
export const getNotFoundSEOMetadata = (): SEOMetadata => ({
  title: 'お知らせが見つかりません | eNEW Studio',
  description: '指定されたお知らせは見つかりませんでした。',
  keywords: [],
  noindex: true,
  canonicalUrl: `${SITE_ORIGIN}/news`,
});

/**
 * 記事の NewsArticle 構造化データ（JSON-LD）
 */
export const generateArticleStructuredData = (
  item: NewsItem
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  headline: item.title,
  datePublished: item.date,
  description: item.summary,
  mainEntityOfPage: `${SITE_ORIGIN}/news/${item.slug}`,
  image: [articleImageUrl(item)],
  publisher: {
    '@type': 'Organization',
    name: 'eNEW Studio',
    logo: { '@type': 'ImageObject', url: LOGO_IMAGE_URL },
  },
});

/**
 * Generate company structured data (JSON-LD)
 */
export const generateCompanyStructuredData = (): CompanyStructuredData => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'eNEW Studio',
    description: defaultSEOMetadata.description,
    url: 'https://enew.studio',
    logo: LOGO_IMAGE_URL,
    foundingDate: '2025-11',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '堀川新町5番1号 アリマビル3F',
      addressLocality: '金沢市',
      addressRegion: '石川県',
      postalCode: '920-0849',
      addressCountry: 'JP',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@enew-studio.co.jp',
    },
    sameAs: [],
  };
};

/**
 * Generate website structured data (JSON-LD)
 */
export const generateWebsiteStructuredData = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'eNEW Studio',
    url: 'https://enew.studio',
    description: defaultSEOMetadata.description,
    publisher: {
      '@type': 'Organization',
      name: 'eNEW Studio',
    },
  };
};

/**
 * Get SEO metadata for a specific route
 * Implements requirements 3.1, 3.2, 3.3, 3.4
 * @param path - The route path (e.g., '/', '/privacy')
 * @returns Complete SEO metadata for the route
 */
export const getSEOMetadataForRoute = (path: string): SEOMetadata => {
  return routeSEOMetadata[path] || routeSEOMetadata['/'];
};
